import React, { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";

export interface PortalProps {
  children?: ReactNode;
  forceRender?: boolean;
  getContainer?: () => Element | null | undefined;
  visible?: boolean;
}

const getDefaultContainer = () => {
  if (typeof document === "undefined") {
    return null;
  }

  return document.body;
};

const Portal = ({
  children,
  forceRender = false,
  getContainer = getDefaultContainer,
  visible = false,
}: PortalProps) => {
  const [activated, setActivated] = useState(() => forceRender || visible);
  const [container, setContainer] = useState<Element | null>(null);

  useEffect(() => {
    if (forceRender || visible) {
      setActivated(true);
    }
  }, [forceRender, visible]);

  useEffect(() => {
    if (!activated) {
      return undefined;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const syncContainer = (shouldRetry: boolean) => {
      const nextContainer = getContainer() || null;
      if (cancelled) {
        return;
      }

      setContainer(nextContainer);

      if (!nextContainer && shouldRetry) {
        retryTimer = setTimeout(() => syncContainer(false), 0);
      }
    };

    syncContainer(true);

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [activated, getContainer]);

  if (!activated || !container) {
    return null;
  }

  return createPortal(children, container);
};

export default Portal;
